import type { Money, MoneyRange } from "../types/index.js";

/** One increment tier: amounts up to (and including) `maxAmount` round to the nearest `increment`. */
export interface RoundingTier {
  maxAmount: number;
  increment: number;
}

/** A configurable set of rounding tiers, ordered ascending by `maxAmount`. The last tier's `maxAmount` should be `Infinity`. */
export interface RoundingConfig {
  tiers: RoundingTier[];
}

/**
 * Default planning-increment rounding. Coarser increments at higher
 * dollar amounts, matching how contractors and homeowners actually talk
 * about renovation budgets - "$50,000 to $75,000," not "$52,340 to
 * $71,890." This is the config, not a hardcoded rule - pass a different
 * `RoundingConfig` to `roundMoneyRange` to use different increments (a
 * per-project-type override, a coarser "quick estimate" mode, etc).
 */
export const DEFAULT_ROUNDING_CONFIG: RoundingConfig = {
  tiers: [
    { maxAmount: 5_000, increment: 250 },
    { maxAmount: 20_000, increment: 1_000 },
    { maxAmount: 75_000, increment: 2_500 },
    { maxAmount: 200_000, increment: 5_000 },
    { maxAmount: Infinity, increment: 10_000 }
  ]
};

function tierFor(amount: number, config: RoundingConfig): RoundingTier {
  const magnitude = Math.abs(amount);
  const tier = config.tiers.find((t) => magnitude <= t.maxAmount);
  // Falls through to the coarsest tier if `tiers` doesn't end in Infinity - a
  // misconfigured table should still produce a sensible (if too coarse) result
  // rather than throw.
  return tier ?? config.tiers[config.tiers.length - 1]!;
}

/** Rounds down to the tier's increment - used for low estimates, so the range never overstates the floor. */
export function roundDownToIncrement(amount: number, config: RoundingConfig = DEFAULT_ROUNDING_CONFIG): number {
  const { increment } = tierFor(amount, config);
  return Math.floor(amount / increment) * increment;
}

/** Rounds up to the tier's increment - used for high estimates, so the range never understates the ceiling. */
export function roundUpToIncrement(amount: number, config: RoundingConfig = DEFAULT_ROUNDING_CONFIG): number {
  const { increment } = tierFor(amount, config);
  return Math.ceil(amount / increment) * increment;
}

/** Rounds to the nearest increment - used for single "typical" figures. */
export function roundNearestToIncrement(amount: number, config: RoundingConfig = DEFAULT_ROUNDING_CONFIG): number {
  const { increment } = tierFor(amount, config);
  return Math.round(amount / increment) * increment;
}

function money(amount: number): Money {
  return { amount, currency: "CAD" };
}

/**
 * Rounds a low/typical/high range to sensible planning increments: low
 * rounds down, high rounds up, typical rounds to nearest - so the range
 * never becomes narrower than the real spread, and no figure carries
 * false cents-level precision.
 */
export function roundMoneyRange(range: MoneyRange, config: RoundingConfig = DEFAULT_ROUNDING_CONFIG): MoneyRange {
  return {
    low: money(roundDownToIncrement(range.low.amount, config)),
    typical: money(roundNearestToIncrement(range.typical.amount, config)),
    high: money(roundUpToIncrement(range.high.amount, config))
  };
}

/** Rounds a single Money figure to the nearest increment (e.g. for a single contingency percentage-derived amount). */
export function roundMoneyNearest(value: Money, config: RoundingConfig = DEFAULT_ROUNDING_CONFIG): Money {
  return money(roundNearestToIncrement(value.amount, config));
}
